import AddButton from "components/Button/AddButton";
import React, { useEffect, useRef, useState } from "react";
import TrashIcon from "assets/icon/TrashIcon";
import { useNavigate } from "react-router-dom";
import { DropdownStatusList, EmptyString, FormTypeAdd, PageSize, StatusCodeActive, StatusCodeDelete } from "utils/Constant";
import EditIcon from "assets/icon/EditIcon";
import Dropdown from "components/Input/Dropdown";
import { addSpaceEvery4, GetStatus, removeNonNumeric } from "utils/helpers";
import RevisionIcon from "assets/icon/RevisionIcon";
import XIcon from "assets/icon/XIcon";
import FilterIcon from "assets/icon/FilterIcon";
import { useCallApi } from "api/apiHooks";
import { useModal } from "utils/context/ModalContext";
import Pagination from "components/Input/PaginationButton";
import { FilterCard, GridDeleteButton, GridEditButton, MainBodyCard, PagesTitle } from "components/Cards";
import Select from 'react-select'
import DataTable from "components/DataTable";
import { CustomerCardService } from "services/CustomerCardService/CustomerCardService";

export default function CustomerCard(){
    let navigate = useNavigate()
    const firstLoad = useRef(true)
    const debounceRef = useRef(null)

    const { showFail, showLoading, hideLoading, showSuccess, confirm } = useModal()
    const { fetchBankList, fetchCardVendorList, fetchDeleteCustomerCard } = useCallApi()
    const { fetchCustomerCardList } = CustomerCardService()

    const [customerCardList, setCustomerCardList] = useState([])
    const [customerName, setCustomerName] = useState(EmptyString)
    const [cardNumber, setCardNumber] = useState(EmptyString)
    const [bankId, setBankId] = useState(EmptyString)
    const [cardVendorId, setCardVendorId] = useState(EmptyString)
    const [bankOptions, setBankOptions] = useState([])
    const [cardVendorOptions, setCardVendorOptions] = useState([])
    const [status, setStatus] = useState(DropdownStatusList[1].value)
    const [isFilterOpen, setIsFilterOpen] = useState(false)
    const [pageIndex, setPageIndex] = useState(0)
    const [totalPages, setTotalPages] = useState(0)

    const columns = [
        {
            key: "rowNumber",
            header: "No",
            headerClassName: "w-16 font-semibold",
            cellClassName: "text-center"
        },
        {
            key: "customerName",
            header: "Nama Pelanggan"
        },
        {
            key: "cardNumber",
            header: "No Kartu",
            render: (row) => addSpaceEvery4(row.cardNumber),
        },
        {
            key: "bankName",
            header: "Bank",
            render: (row) => row.bankName.toUpperCase(),
        },
        {
            key: "cardVendorName",
            header: "Vendor",
        },
        {
            key: "mdr",
            header: "MDR",
        },
        {
            key: "mdrQris",
            header: "MDR QRIS",
        },
        {
            key: "mdrPl",
            header: "MDR PL",
        },
        {
            key: "mdrPlQris",
            header: "MDR PL QRIS",
        },
        {
            key: "dueDate",
            header: "Tgl JT",
        },
        {
            key: "status",
            header: "Status",
            render: (row) => GetStatus(row.status),
        },
        {
            key: "action",
            header: "Action",
            render: (row) => (
                <div className="flex gap-2">
                    <GridEditButton isActive={row.status} onClick={() => editBtnClick(row.customerCardId)} size="w-3" />
                    <GridDeleteButton isActive={row.status} onClick={() => deleteBtnClick(row.customerCardId, row.status)} size="w-3" />
                </div>
            ),
        },
    ]
    
    const isFilterOpenClick = () => {
        setIsFilterOpen(!isFilterOpen)
    }

    const handlePageChange = async (newIndex) => {
        try {
            showLoading()

            const res = await fetchCustomerCardList(
                customerName, 
                cardNumber, 
                bankId === EmptyString ? 0 : parseInt(bankId), 
                cardVendorId === EmptyString ? 0 : parseInt(cardVendorId), 
                status, 
                PageSize, 
                parseInt(newIndex)
            )

            if (!res) return

            const totalData = res?.data?.totalData ?? 0;
            const total = Math.max(1, Math.ceil(totalData / PageSize));

            setCustomerCardList(res)
            setTotalPages(total);
            setPageIndex(newIndex);
        } finally {
            hideLoading();
        }
    }

    const filterEnterKeyPress = (e) => {
		if(e.key === 'Enter'){
			handlePageChange(0)
		}
	}

    const addBtnClick = () => {
        navigate("/customer-card/form?customerCardId=&formType=" + FormTypeAdd)
    }
    const editBtnClick = (customerCardId) => {
        navigate("/customer-card/form?customerCardId=" + customerCardId + "&formType=Edit")
    }
    const deleteBtnClick = async (customerCardId, status) => {
        const isConfirmed = await confirm()

        if (isConfirmed) {
            showLoading()

            const res = await fetchDeleteCustomerCard(customerCardId, status)

            if (!res) return

            showSuccess()
            handlePageChange(pageIndex)
        }
    }

    const getBankList = async () => {
        const res = await fetchBankList('', '', '1', 10000, 0)

        if (!res) return

        const data = res.data.listData
        const arr = [
            { label: "All", value: "" },
            ...data.map(item => ({
                label: `${item.bankCode} - ${item.bankName}`,
                value: String(item.bankId)
            }))
        ];

        setBankOptions(arr)
        setBankId(arr[0].value)
    }
    const getVendorList = async () => {
        const res = await fetchCardVendorList('', '1', 1000, 0)
        if (!res) return

        const data = res.data.listData
        const arr = [
            { label: "All", value: "" },
            ...data.map(item => ({
                label: item.cardVendorName,
                value: String(item.cardVendorId)
            }))
        ]

        setCardVendorOptions(arr)
        setCardVendorId(arr[0].value)
    }

    useEffect(() => {
        const onLoad = async () => {
            if (firstLoad.current) {
                showLoading()

                // Get Bank List
                await getBankList()

                // Get Vendor List
                await getVendorList()

                handlePageChange(0)

                firstLoad.current = false
            }
            else {
                if (debounceRef.current) {
                    clearTimeout(debounceRef.current)
                }
        
                debounceRef.current = setTimeout(() => {
                    handlePageChange(0)
                }, 500)
            }
        }

        onLoad()
    },[customerName, cardNumber, bankId, cardVendorId, status])

    return(
        <MainBodyCard>
            <PagesTitle title='Kartu Pelanggan' />

            <div className="xl:hidden mt-2 flex justify-end">
                <button className="bg-black rounded-md flex gap-2 px-4 py-2" onClick={isFilterOpenClick}>
                    {isFilterOpen ? <XIcon className="fill-white w-6" /> : <FilterIcon className="fill-white w-6" />}
                    <span className="text-white">Filter</span>
                </button>
            </div>

            <div className={`flex flex-col xl:flex-row gap-5 justify-between mt-2 ${isFilterOpen ? 'flex' : 'hidden xl:flex'}`}>
                <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:gap-4 w-full">
                    <FilterCard title='Nama Pelanggan' width='w-full sm:w-56'>
                        <input className="input" value={customerName} onChange={(e) => { setCustomerName(e.target.value.toUpperCase()) }} onKeyDown={filterEnterKeyPress} />
                    </FilterCard>
                    <FilterCard title='Nomor Kartu' width='w-full sm:w-56'>
                        <input className="input" value={cardNumber} onChange={(e) => { setCardNumber(removeNonNumeric(e.target.value)) }} onKeyDown={filterEnterKeyPress} />
                    </FilterCard>
                    <FilterCard title='Bank' width='w-full sm:w-56'>
                        <Select className='w-full' value={bankOptions.find(opt => opt.value === bankId)} options={bankOptions} onChange={(e) => { setBankId(e.value) }} placeholder={"All"} />
                    </FilterCard>
                    <FilterCard title='Vendor' width='w-full sm:w-56'>
                        <Select className='w-full' value={cardVendorOptions.find(opt => opt.value === cardVendorId)} options={cardVendorOptions} onChange={(e) => { setCardVendorId(e.value) }} placeholder={"All"} />
                    </FilterCard>
                    <FilterCard title='Status' width='w-full sm:w-32'>
                        <Dropdown className='w-full' value={status} options={DropdownStatusList} onChange={(e) => { setStatus(e.value) }} placeholder={"All"} />
                    </FilterCard>
                </div>
            </div>

            <div className="mt-3 flex justify-end">
                <AddButton onClick={addBtnClick} title='Tambah Kartu Pelanggan' />
            </div>
            
            <div className="hidden xl:block mt-4">
                <DataTable
                    columns={columns}
                    data={customerCardList?.data?.listData ?? []}
                />
            </div>

            <div className="block xl:hidden mt-4">
                <div className="flex flex-col gap-3">
                    {customerCardList?.data?.listData?.map((data, i) => {
                        return(
                            <div className="bg-white p-3 border shadow flex justify-between">
                                <div className="flex flex-col">
                                    <label>{data.customerName.toUpperCase()}</label>
                                    <label>{data.cardNumber}</label>
                                    <label>{data.bankName + " - " + data.cardVendorName}</label>
                                </div>
                                <div className="flex flex-col justify-between">
                                    <button className="button flex gap-2" onClick={() => editBtnClick(data.customerCardId)}>
                                        <EditIcon className="w-4 fill-white" />
                                    </button>
                                    <button className={`button flex gap-2 ${ data.status === StatusCodeDelete ? "hidden" : "block" }`} onClick={() => deleteBtnClick(data.customerCardId, data.status)}>
                                        <TrashIcon className="w-4 fill-white" />
                                    </button>
                                    <button className={`button flex gap-2 ${ data.status === StatusCodeActive ? 'hidden' : 'block' }`} onClick={() => deleteBtnClick(data.customerCardId, data.status)}>
                                        <RevisionIcon className="w-4 fill-white" />
                                    </button>
                                </div>
                            </div>
                        )
                    })}
                </div>
            </div>

            <Pagination pageIndex={pageIndex} totalPages={totalPages} onPageChange={handlePageChange} />
        </MainBodyCard>
    )
}